import { useState } from "react";
import { Menu, X, Globe } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-1">
        <div className="h-9 w-16 bg-muted-light animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted-light animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted-light animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-foreground hover:text-primary hover:bg-muted-light transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function HeaderMenu() {
  return <MenuProvider menu_id="17">
      <HeaderMenuConsumer />
    </MenuProvider>;
}
export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const languages = [{
    code: "de",
    label: "Deutsch"
  }, {
    code: "en",
    label: "English"
  }];
  const handleLanguageChange = (code: string) => {
    console.log(`Language changed to ${code}`);
  };
  return <header className="bg-background border-b border-border sticky top-0 z-50 shadow-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center focus-ring">
            <img src="https://muensterland-industrieservice.com/wp-content/uploads/2025/10/LogoPage.png" alt="Muensterland Industrieservice UG" className="h-14" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <HeaderMenu />

            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 focus-ring">
                  <Globe className="size-4" />
                  <span className="text-sm font-medium">DE</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {languages.map((lang, i) => <DropdownMenuItem key={lang.code} data-index={i} onClick={() => handleLanguageChange(lang.code)}>
                    {lang.label}
                  </DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="focus-ring">
                  <Globe className="size-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {languages.map((lang, i) => <DropdownMenuItem key={lang.code} data-index={i} onClick={() => handleLanguageChange(lang.code)}>
                    {lang.label}
                  </DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="focus-ring">
              {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && <div className="md:hidden py-4 border-t border-border animate-fade-in-up">
            <nav className="flex flex-col gap-2">
              <MenuProvider menu_id="17">
                <MobileMenuItems setMobileMenuOpen={setMobileMenuOpen} />
              </MenuProvider>
            </nav>
          </div>}
      </div>
    </header>;
}
function MobileMenuItems({
  setMobileMenuOpen
}: {
  setMobileMenuOpen: (open: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <>
        <div className="h-10 bg-muted-light animate-pulse rounded-md" />
        <div className="h-10 bg-muted-light animate-pulse rounded-md" />
        <div className="h-10 bg-muted-light animate-pulse rounded-md" />
      </>;
  }
  return <>
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-base font-medium text-foreground hover:bg-muted-light hover:text-primary rounded-md transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] focus-ring">
          {item.label}
        </Link>)}
    </>;
}